import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CONFIGURATOR_STEPS } from '../config/configurator-steps.config';
import {
  EConfiguratorStep,
  IConfiguratorAnswers,
  IConfiguratorDraft,
  IConfiguratorErrors,
  IConfiguratorStep
} from '../models/configurator.model';
import { normalizeAnswer, validateStep } from '../helpers/configurator-rules.helper';
import { getAnswer, setAnswer } from '../helpers/configurator-answer-path.helper';
import { CONFIGURATOR_DRAFT_VERSION } from './configurator-persistence.adapter';
import { ConfiguratorPersistenceService } from './configurator-persistence.service';

/** Single source of truth of the wizard: answers, navigation and validation errors. */
@Injectable()
export class ConfiguratorStoreService {
  private readonly persistence = inject(ConfiguratorPersistenceService);

  readonly steps: IConfiguratorStep[] = CONFIGURATOR_STEPS.filter((step) => step.enabled);

  readonly answers = signal<IConfiguratorAnswers>({});
  readonly currentStep = signal(0);
  readonly maxStep = signal(0);
  readonly errors = signal<IConfiguratorErrors>({});
  /** Counts failed validations, so questions with an error shake again on each failed "Weiter". */
  readonly validationFailures = signal(0);
  readonly ready = signal(false);
  /**
   * Once the review was reached, going back to a step is an edit round-trip: "Speichern" validates the step
   * and returns to the review.
   */
  readonly editReturn = signal(false);
  readonly submittedAt = signal<string | null>(null);
  readonly submitted = computed(() => !!this.submittedAt());

  readonly step = computed(() => this.steps[this.currentStep()]);
  readonly isFirstStep = computed(() => this.currentStep() === 0);
  readonly isLastStep = computed(() => this.currentStep() === this.steps.length - 1);
  readonly reviewIndex = this.steps.findIndex((step) => step.id === EConfiguratorStep.REVIEW);
  readonly isReview = computed(() => this.step().id === EConfiguratorStep.REVIEW);

  init(contextKey: string): Observable<void> {
    this.ready.set(false);

    return this.persistence.load(contextKey).pipe(
      map((draft) => {
        if (draft) this.restore(draft);
        this.ready.set(true);
      })
    );
  }

  getAnswer(path: string): unknown {
    return getAnswer(this.answers(), path);
  }

  /** `path` is a question id or a nested path like `bathrooms.1.shower`. */
  setAnswer(path: string, value: unknown): void {
    this.answers.set(setAnswer(this.answers(), path, normalizeAnswer(value)));
    this.clearError(path);
    this.persist();
  }

  /** Replaces all answers at once, e.g. after removing a bathroom or to undo that. */
  replaceAnswers(answers: IConfiguratorAnswers): void {
    this.answers.set(answers);
    this.errors.set({});
    this.persist();
  }

  /** Validates the current step. Returns false and exposes errors when it is not complete. */
  next(): boolean {
    const errors = validateStep(this.step(), this.answers());
    this.errors.set(errors);
    if (Object.keys(errors).length) {
      this.validationFailures.update((count) => count + 1);
      return false;
    }

    if (this.editReturn() && this.reviewIndex >= 0) {
      this.editReturn.set(false);
      this.currentStep.set(this.reviewIndex);
      this.persist();
      return true;
    }

    if (!this.isLastStep()) {
      this.currentStep.update((step) => step + 1);
      this.maxStep.update((max) => Math.max(max, this.currentStep()));
      this.persist();
    }
    return true;
  }

  back(): void {
    if (this.isFirstStep()) return;
    this.errors.set({});
    this.currentStep.update((step) => step - 1);
    this.persist();
  }

  goTo(index: number): void {
    if (this.submitted() || index < 0 || index > this.maxStep() || index === this.currentStep()) return;
    this.errors.set({});
    this.editReturn.set(
      this.reviewIndex >= 0 && this.maxStep() >= this.reviewIndex && index < this.reviewIndex
    );
    this.currentStep.set(index);
    this.persist();
  }

  /**
   * Validates every step before the review. The first incomplete one is opened with its errors (and "Speichern"
   * leads back to the review); returns false then. Otherwise the request counts as sent.
   */
  submit(): boolean {
    const answers = this.answers();
    const invalidIndex = this.steps.findIndex(
      (step, index) => index < this.currentStep() && Object.keys(validateStep(step, answers)).length > 0
    );

    if (invalidIndex >= 0) {
      this.currentStep.set(invalidIndex);
      this.editReturn.set(true);
      this.errors.set(validateStep(this.steps[invalidIndex], answers));
      this.validationFailures.update((count) => count + 1);
      this.persist();
      return false;
    }

    this.errors.set({});
    this.editReturn.set(false);
    this.submittedAt.set(new Date().toISOString());
    this.persist();
    return true;
  }

  /** "Neue Anfrage": an empty configurator. */
  reset(): void {
    this.answers.set({});
    this.errors.set({});
    this.editReturn.set(false);
    this.submittedAt.set(null);
    this.maxStep.set(0);
    this.currentStep.set(0);
    this.persist();
  }

  clearError(id: string): void {
    if (!this.errors()[id]) return;
    const { [id]: _, ...errors } = this.errors();
    this.errors.set(errors);
  }

  private restore(draft: IConfiguratorDraft): void {
    const lastIndex = this.steps.length - 1;
    const stepIndex = this.steps.findIndex((step) => step.id === draft.currentStep);
    const maxStep = Math.min(Math.max(draft.maxStep || 0, 0), lastIndex);

    this.answers.set(draft.answers || {});
    this.maxStep.set(maxStep);
    this.currentStep.set(stepIndex >= 0 ? Math.min(stepIndex, maxStep) : 0);
    this.submittedAt.set(draft.submittedAt || null);
    // An edit round-trip ends with a reload; the step is then left as usual.
    this.editReturn.set(false);
  }

  private persist(): void {
    this.persistence.schedule({
      version: CONFIGURATOR_DRAFT_VERSION,
      answers: this.answers(),
      currentStep: this.step().id,
      maxStep: this.maxStep(),
      savedAt: new Date().toISOString(),
      ...(this.submittedAt() ? { submittedAt: this.submittedAt()! } : {})
    });
  }
}
